/// <amd-module name="Build/Queue-Panel/QueuePanel" />
import { BuildDefinition, BuildDefinitionVariable, BuildRepository, DesignerProcess } from "Build/Client/Contracts/Build";
import {
    BuildResourceData,
    ContainerResourceData,
    GeneralSettingsData,
    OrgSettingsData,
    PackageResourceData,
    PipelineResourceData,
    RepositoryResourceData
} from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";
import { BranchDropdown } from "Build/Common/Library/Legacy/Components/BranchDropdown";
import { ShelvesetPickerActionHub } from "Build/Common/Library/Legacy/Components/ShelvesetAction";
import { QueueActionCreator, QueueActionHub, QueueStore } from "Build/Common/Library/Legacy/Stores/Queue";
import { getSingletonManager } from "Build/Common/Library/SingletonManager";
import {
    EnableDiagnosticActionCreator,
    EnableDiagnosticActionHub,
    IBuildQueuedPayload,
    IQueueBuildPayload,
    QueueBuildActionCreator,
    QueueBuildActionHub,
    SourceVersionActionCreator,
    SourceVersionActionHub
} from "Build/Queue-Panel/Action";
import { DemandsControllerView, IDemandsControllerViewProps } from "Build/Queue-Panel/DemandsControllerView";
import * as Resources from "Build/Queue-Panel/Resources";
import { IResourcesControllerViewProps, ResourcesControllerView } from "Build/Queue-Panel/ResourcesControllerView";
import { IStageOptionsControllerViewProps, StageOptionsControllerView } from "Build/Queue-Panel/StageOptionsControllerView";
import { QueueBuildStore } from "Build/Queue-Panel/Stores/QueueBuild";
import { TemplateParametersView } from "Build/Queue-Panel/TemplateParametersView";
import { IVariablesControllerViewProps, VariablesControllerView } from "Build/Queue-Panel/VariablesControllerView";
import { RepositoryTypes } from "Build/SourceProviders/Common";
import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";
import { AgentQueueSelector } from "DistributedTask/Common/Library/AgentQueueSelector";
import { IAgentSpecification, IAgentSpecificationService } from "DistributedTask/Common/Library/Sources/AgentSpecificationService";
import { QueueSource } from "DistributedTask/Common/Library/Sources/Queue";
import { BuildRequiredPermissions, Security } from "Build/Common/Library/Security";
import { PermissionsStore } from "Build/Common/Library/Stores/Permissions";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import {
    BuildResourceParameters,
    ContainerResourceParameters,
    PackageResourceParameters,
    PipelineResourceParameters,
    RepositoryResourceParameters
} from "Pipelines/Client/Contracts/Pipelines";
import * as React from "react";
import { IAdvancedOptionListItem, IDemandData, IDictionaryStringTo, IPipelineStageOption, IVariableData } from "Types";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { equals, format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Checkbox } from "VSSUI/Checkbox";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon, IconSize } from "VSSUI/Icon";
import { IIntersectionContext, Intersection, IntersectionContext } from "VSSUI/Intersection";
import { IListItemDetails, IListRow, List, ListItem, ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelContent, PanelFooter, PanelOverlay } from "VSSUI/Panel";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";

const branchRefPrefix = "refs/heads/";
const tagRefPrefix = "refs/tags/";

export interface IQueuePanelState {
    showPanel: boolean;
    queues: TaskAgentQueue[];
    selectedAgentQueue: TaskAgentQueue;
    sourceVersion: string;
    shelvesetName: string;
    currentCommit: string;
    enableDiagnostic: boolean;
    showError: boolean;
    errorMessage: string;
    messageSeverity: MessageCardSeverity;
    agentSpecifications: IAgentSpecification[];
    isLoadingAgentSpecifications: boolean;
    selectedAgentSpecification?: IAgentSpecification;
    overflowing: boolean;
}

export interface IQueuePanelProps {
    showPanel: boolean;
    pageContext: IVssPageContext;
    projectId: string;
    runtimeVariables: any;
    previousTemplateParameters?: { [key: string]: string };
    definitionVariables?: { [key: string]: BuildDefinitionVariable };
    definitionId: number;
    buildDefinition?: BuildDefinition;
    buildRepository: BuildRepository;
    sourceBranch: string;
    isSaveEnabled?: boolean;
    onDismiss(): void;
    onSave?(comment: string): Promise<BuildDefinition>;
}

export class QueuePanel extends React.Component<IQueuePanelProps, IQueuePanelState> {
    private static YamlProcessType = 2;

    private _actionCreator: QueueBuildActionCreator;
    private _actionHub: QueueBuildActionHub;
    private _advancedOptionsItemProvider = new ObservableArray<IAdvancedOptionListItem>([]);
    private _advancedOptionsListSelection = new ListSelection();
    private _buildDefinition: BuildDefinition;
    private _showPanel: boolean = false;
    private _showError: boolean = false;
    private _errorMessage: string = "";
    private _messageSeverity: MessageCardSeverity;
    private _queueStore: QueueStore;
    private _queueBuildStore: QueueBuildStore;
    private _queues: TaskAgentQueue[];
    private _selectedAgentQueue: TaskAgentQueue;
    private _enableDiagnosticActionCreator: EnableDiagnosticActionCreator;
    private _enableDiagnosticActionHub: EnableDiagnosticActionHub;
    private _sourceVersionActionCreator: SourceVersionActionCreator;
    private _sourceVersionActionHub: SourceVersionActionHub;
    private _queueActionCreator: QueueActionCreator;
    private _queueActionHub: QueueActionHub;
    private _shelvesetPickerActionHub: ShelvesetPickerActionHub;
    private _sourceVersion: string;
    private _shelvesetName: string;
    private _currentCommit: string;
    private _enableDiagnostic: boolean = false;
    private _agentSpecificationService: IAgentSpecificationService;
    private _agentSpecificationServiceErrorMessage = new ObservableValue<string>("");
    private _agentSpecifications: IObservableArray<IAgentSpecification> = new ObservableArray();
    private _isLoadingAgentSpecifications = new ObservableValue<boolean>(false);
    private _selectedAgentSpecification?: IAgentSpecification;
    private _runButtonDisabled = new ObservableValue<boolean>(true);
    private _saveCommentText = new ObservableValue<string>("");
    private _queueActionPending = new ObservableValue<boolean>(false);
    private _timerManagement = new TimerManagement();
    private _pendingOverlayTimeoutId = 0;
    private _isTfsGit: boolean = false;
    private _commentTextFieldRef: React.RefObject<TextField> = React.createRef<TextField>();
    private _overflowing: boolean = false;
    private _observingOverflow: boolean = false;
    private _intersectionContext: IIntersectionContext;
    private _intersectionCallbackRegistered: boolean = false;
    private _observedElement: HTMLDivElement | null;
    private _wasDefinitionPreviouslySaved: boolean = false;
    private _stageChooserDisabled: boolean = false;
    private _enforceSettableVar: boolean = false;
    private _enableEditPipelineQueueConfigurationPermission: boolean = false;
    private _security: Security;
    private _permissions: PermissionsStore;

    private _shortcutService: IVssKeyboardShortcutService;
    constructor(props: IQueuePanelProps) {
        super(props);
        this._enableEditPipelineQueueConfigurationPermission = isFeatureFlagEnabled(props.pageContext, "Build2.EditPipelineQueueConfigurationPermission", false);
    
        this._permissions = new PermissionsStore({
            pageContext: props.pageContext
        });

        this._security = new Security({
            projectId: props.projectId,
            store: this._permissions
        });

        this._shortcutService = props.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");

        this._showPanel = props.showPanel;
        this._wasDefinitionPreviouslySaved = this.props.definitionId > 0;

        // if disableDelete is undefined, it's a build definition variable so set disableDelete to true
        if (this.props.runtimeVariables && this.props.runtimeVariables.length > 0) {
            this.props.runtimeVariables.forEach((v: any) => {
                v.variable.disableDelete = v.variable.disableDelete === undefined ? true : v.variable.disableDelete;
            });
        }

        this._isTfsGit =
            this.props.buildRepository &&
            this.props.buildRepository.type &&
            this.props.buildRepository.type.toLowerCase() === RepositoryTypes.TfsGit.toLowerCase()
                ? true
                : false;

        this._actionHub = new QueueBuildActionHub();
        this._actionCreator = new QueueBuildActionCreator({ actionHub: this._actionHub });

        let currentStore = getSingletonManager().get<QueueStore>(QueueStore.key);
        if (!currentStore) {
            this._queueActionHub = new QueueActionHub();
            currentStore = new QueueStore({
                pageContext: props.pageContext,
                project: props.projectId,
                actionHub: this._queueActionHub
            });

            getSingletonManager().add<QueueStore>(QueueStore.key, currentStore);
        } else {
            // If the store already exists fetch the queues
            this._queues = currentStore.getQueues();
        }

        this._queueStore = currentStore;
        this._queueActionHub = currentStore.getActionHub();

        this._queueActionCreator = new QueueActionCreator({
            actionHub: this._queueActionHub,
            source: new QueueSource({
                pageContext: this.props.pageContext,
                project: this.props.projectId
            })
        });
        let store = getSingletonManager().get<QueueBuildStore>(QueueBuildStore.key);
        if (!store) {
            this._sourceVersionActionHub = new SourceVersionActionHub();
            this._shelvesetPickerActionHub = new ShelvesetPickerActionHub();
            this._enableDiagnosticActionHub = new EnableDiagnosticActionHub();

            store = new QueueBuildStore({
                pageContext: props.pageContext,
                projectId: props.projectId,
                definitionId: props.definitionId,
                buildDefinition: props.buildDefinition,
                sourceBranch: props.sourceBranch,
                runtimeVariables: this.props.runtimeVariables,
                previousTemplateParameters: this.props.previousTemplateParameters || {},
                definitionVariables: this.props.definitionVariables,
                sourceVersionActionHub: this._sourceVersionActionHub,
                shelvesetPickerActiobHub: this._shelvesetPickerActionHub,
                enableDiagnosticActionHub: this._enableDiagnosticActionHub,
                queueActionHub: this._queueActionHub,
                buildRepository: props.buildRepository
            });

            getSingletonManager().add<QueueBuildStore>(QueueBuildStore.key, store);
        } else {
            this._sourceVersionActionHub = store.getSourceControlSelectorActionHub();
            this._shelvesetPickerActionHub = store.getShelvesetPickerActionHub();
            this._enableDiagnosticActionHub = store.getEnableDiagnosticActionHub();
            if (props.buildDefinition) {
                store.updateBuildDefinition(props.buildDefinition, props.runtimeVariables, undefined, props.previousTemplateParameters);
            } else {
                store.updateDefinition(
                    props.definitionId,
                    props.runtimeVariables,
                    props.sourceBranch,
                    false,
                    props.buildRepository,
                    props.definitionVariables,
                    props.previousTemplateParameters
                );
            }
        }

        this._queueBuildStore = store;

        this._sourceVersionActionCreator = new SourceVersionActionCreator({ actionHub: this._sourceVersionActionHub });
        this._enableDiagnosticActionCreator = new EnableDiagnosticActionCreator({ actionHub: this._enableDiagnosticActionHub });

        this.state = this._getState();

        this._queueBuildStore.fetchDemands();

        Promise.all([this._setupAgentSpecifications(), this._setupOrgSettings(), this._setupProjectSettings()]).then(() =>
            this._registerAdvancedOptions()
        );
    }

    public componentDidMount() {
        this._actionHub.buildQueued.addListener(this._handleBuildQueued);
        this._queueStore.addListener(this._queueStoreChanged);
        this._queueBuildStore.addListener(this._storeChanged);
    }

    public componentWillUnmount() {
        this._actionHub.buildQueued.removeListener(this._handleBuildQueued);
        this._queueStore.removeListener(this._queueStoreChanged);
        this._queueBuildStore.removeListener(this._storeChanged);

        this._timerManagement.dispose();

        if (this._intersectionContext) {
            this._intersectionContext.unregister(this._onIntersect);
        }
    }

    public componentDidUpdate() {
        // _intersectionContext may not be initialized when componentDidMount is called
        if (this._intersectionContext && !this._intersectionCallbackRegistered) {
            this._intersectionContext.register(this._onIntersect);
            this._intersectionCallbackRegistered = true;
        }

        this.props.isSaveEnabled && this._commentTextFieldRef && this._commentTextFieldRef.current && this._commentTextFieldRef.current.focus();
    }

    public render(): JSX.Element {
        const isYamlBuildDefinition: boolean = this._isYamlBuildDefinition();
        const showLoading = !this._buildDefinition;
        const runButtonText: string = this._getRunButtonText();
        const commandBarItems: IHeaderCommandBarItem[] = [];
        const templateParametersEnabled = isYamlBuildDefinition && !!this._queueBuildStore.templateParameters && this._wasDefinitionPreviouslySaved;

        commandBarItems.push({
            id: "queue-panel-close-button",
            onActivate: this._dismissPanel,
            ariaLabel: Resources.CloseButtonLabel,
            iconProps: { iconName: "Cancel" },
            subtle: true,
            role: "button"
        });

        this._shortcutService.setShortcutsEnabled(false);

        return (
            <Observer isPanelOpen={this._showPanel} queueActionPending={this._queueActionPending}>
                {(props: { isPanelOpen: boolean; queueActionPending: boolean }) => {
                    return props.isPanelOpen ? (
                        <CustomPanel
                            className="queue-panel flex-column h-scroll-hidden"
                            lightDismiss={!props.queueActionPending}
                            onDismiss={this._dismissPanel}
                        >
                            <Header
                                className="bolt-panel-header title-m"
                                commandBarItems={commandBarItems}
                                title={Resources.QueuePanelTitleNoDefinition}
                                description={Resources.QueuePanelDescription}
                            />
                            <PanelContent>
                                <Intersection>
                                    <div
                                        className={css(
                                            this.state.overflowing && "bottom-border",
                                            "custom-scrollbar flex-column flex-grow v-scroll-auto"
                                        )}
                                    >
                                        <IntersectionContext.Consumer>
                                            {(intersectionContext: IIntersectionContext) => {
                                                this._intersectionContext = intersectionContext;

                                                return (
                                                    <div className="flex-column flex-grow" ref={element => this._observeElement(element)}>
                                                        <Observer storeError={this._queueBuildStore.errorMessage}>
                                                            {(props: { storeError: string }) =>
                                                                props.storeError ? (
                                                                    <div className="margin-vertical-8 padding-horizontal-20">
                                                                        <MessageCard severity={MessageCardSeverity.Error}>
                                                                            <span className="multiline-error">{props.storeError}</span>
                                                                        </MessageCard>
                                                                    </div>
                                                                ) : null
                                                            }
                                                        </Observer>
                                                        {this.state.showError && (
                                                            <div className="margin-vertical-8 padding-horizontal-20">
                                                                <MessageCard severity={this.state.messageSeverity}>
                                                                    <span className="multiline-error">{this.state.errorMessage}</span>
                                                                </MessageCard>
                                                            </div>
                                                        )}
                                                        {props.isPanelOpen && !showLoading && (
                                                            <>
                                                                <div className="padding-horizontal-20">
                                                                    {!isYamlBuildDefinition && (
                                                                        <>
                                                                            {this._getSaveCommentContent()}
                                                                            {this._getAgentPools()}
                                                                        </>
                                                                    )}
                                                                    {this._getBranchControl()}
                                                                </div>
                                                                <div className="flex-column rhythm-vertical-16">
                                                                    {templateParametersEnabled && (
                                                                        <TemplateParametersView
                                                                            allowEditing={this._enableEditPipelineQueueConfigurationPermission? this._security.canEditPipelineQueueConfiguration(this._buildDefinition): true}
                                                                            className="padding-horizontal-20"
                                                                            store={this._queueBuildStore}
                                                                        />
                                                                    )}

                                                                    <h2 className="padding-horizontal-20 primary-text title-s margin-vertical-0">
                                                                        {Resources.AdvancedOptionsLabel}
                                                                    </h2>
                                                                    <List
                                                                        ariaLabel={Resources.AdvancedOptionsLabel}
                                                                        itemProvider={this._advancedOptionsItemProvider}
                                                                        onSelect={this._onAdvancedOptionSelected}
                                                                        renderRow={this._renderAdvancedOptionRow}
                                                                        selection={this._advancedOptionsListSelection}
                                                                        width="100%"
                                                                    />
                                                                </div>
                                                            </>
                                                        )}
                                                        {showLoading && props.isPanelOpen && <Spinner className="queue-build-spinner" />}
                                                    </div>
                                                );
                                            }}
                                        </IntersectionContext.Consumer>
                                    </div>
                                </Intersection>
                            </PanelContent>
                            <PanelFooter className="flex-row">
                                <Checkbox
                                    label={Resources.RunWithDiagnostics}
                                    className="flex-grow margin-right-8"
                                    onChange={this._onToggleEnableDiagnostic}
                                    checked={this.state.enableDiagnostic}
                                />
                                <ButtonGroup className="bolt-panel-footer-buttons">
                                    <Button text={Resources.CancelButtonText} onClick={this._dismissPanel} />
                                    <Observer
                                        runButtonDisabled={this._runButtonDisabled}
                                        templateParametersAreValid={this._queueBuildStore.templateParametersAreValid}
                                    >
                                        {(props: { runButtonDisabled: boolean; templateParametersAreValid: boolean }) => (
                                            <Button
                                                text={runButtonText}
                                                primary={true}
                                                onClick={this._queueBuild}
                                                disabled={props.runButtonDisabled || (templateParametersEnabled && !props.templateParametersAreValid)}
                                            />
                                        )}
                                    </Observer>
                                </ButtonGroup>
                            </PanelFooter>
                            {props.queueActionPending && <PanelOverlay overlayContent={<Spinner label={Resources.StartingRun} />} />}
                        </CustomPanel>
                    ) : null;
                }}
            </Observer>
        );
    }

    private async _setupAgentSpecifications(): Promise<void> {
        this._agentSpecificationService = this.props.pageContext.getService<IAgentSpecificationService>("IAgentSpecificationService");
        return this._queueBuildStore
            .fetchBuildDefinition()
            .then((buildDefinition: BuildDefinition) => {
                this._buildDefinition = buildDefinition;
                const queue = buildDefinition.queue;
                if (queue && buildDefinition.project) {
                    this._selectedAgentQueue = {
                        id: queue.id,
                        name: queue.name,
                        pool: queue.pool,
                        projectId: buildDefinition.project.id
                    } as TaskAgentQueue;
                }

                this._queueActionCreator.selectQueue(this._selectedAgentQueue);
                if (!this._isYamlBuildDefinition()) {
                    this._updateAgentSpecifications(this._selectedAgentQueue);
                    const buildProcess: DesignerProcess = buildDefinition.process as DesignerProcess;
                    if (buildProcess && buildProcess.target && buildProcess.target.agentSpecification) {
                        this._selectedAgentSpecification = buildProcess.target.agentSpecification as IAgentSpecification;
                    }
                }
                this._runButtonDisabled.value = false;
                this.setState(this._getState());
            })
            .catch((reason: Error) => {
                if (reason && reason.message) {
                    this._errorMessage = reason.message;
                    this._showError = true;
                    this.setState(this._getState());
                }
            });
    }

    private async _setupOrgSettings(): Promise<void> {
        return this._queueBuildStore.fetchOrgSettings().then((settings: OrgSettingsData) => {
            if (settings) {
                this._stageChooserDisabled = settings.disableStageChooser;
            }
        });
    }

    private async _setupProjectSettings(): Promise<void> {
        return this._queueBuildStore.fetchProjSettings().then((settings: GeneralSettingsData) => {
            if (settings) {
                this._enforceSettableVar = settings.enforceSettableVar.enabled;
            }
        });
    }

    private _isYamlBuildDefinition(): boolean {
        return this._buildDefinition && this._buildDefinition.process ? this._buildDefinition.process.type === QueuePanel.YamlProcessType : false;
    }

    private _updateAgentSpecifications(queue: TaskAgentQueue): void {
        this._isLoadingAgentSpecifications.value = true;
        this._runButtonDisabled.value = true;
        this.setState(this._getState());
        this._agentSpecificationService
            .getAgentSpecificationsByQueue(queue)
            .then((agentSpecifications: IAgentSpecification[]) => {
                this._agentSpecificationServiceErrorMessage.value = "";
                this._agentSpecifications.removeAll();
                if (agentSpecifications && agentSpecifications.length > 0) {
                    this._agentSpecifications.push(...agentSpecifications);
                    if (!this._selectedAgentSpecification) {
                        this._agentSpecificationServiceErrorMessage.value = Resources.AgentSpecificationRequired;
                    }
                }

                this._runButtonDisabled.value = !!this._agentSpecificationServiceErrorMessage.value;
            })
            .catch((error: Error) => {
                this._agentSpecifications.removeAll();
                if (error && error.message) {
                    this._agentSpecificationServiceErrorMessage.value = error.message;
                }
            })
            .then(() => {
                this._isLoadingAgentSpecifications.value = false;
                this.setState(this._getState());
            });
    }

    private _renderAdvancedOptionRow = (rowIndex: number, item: IAdvancedOptionListItem, details: IListItemDetails<IAdvancedOptionListItem>) => {
        const isEnabled = this._advancedOptionsListSelection.selectable(rowIndex);
        const secondaryText = isEnabled ? item.secondaryText : Resources.DisabledDueToInvalidTemplateParametersMessage;
        return (
            <ListItem
                className={isEnabled ? "cursor-pointer" : "advanced-option-disabled"}
                key={"list-item" + rowIndex}
                index={rowIndex}
                details={details}
            >
                <div className="row-width flex-grow flex-row padding-horizontal-20">
                    <div className="text-width flex-column flex-grow padding-vertical-8">
                        <span className="primary-text">{item.primaryText}</span>
                        <span className="flex-row scroll-hidden flex-center font-size-ms secondary-text">{secondaryText}</span>
                    </div>
                    <Icon iconName="ChevronRightMed" size={IconSize.medium} />
                </div>
            </ListItem>
        );
    };

    private _getSaveCommentContent() {
        if (!this.props.isSaveEnabled) {
            return null;
        }

        return (
            <>
                <div className="flex-column">
                    <label className="label">{Resources.SaveComment}</label>
                    <TextField
                        ariaLabel={Resources.SaveComment}
                        value={this._saveCommentText}
                        onChange={this._onSaveCommentChange}
                        ref={this._commentTextFieldRef}
                    />
                </div>
                <hr />
            </>
        );
    }

    private _getAgentPools() {
        return (
            <Observer
                agentSpecifications={this._agentSpecifications}
                errorMessage={this._agentSpecificationServiceErrorMessage}
                isLoading={this._isLoadingAgentSpecifications}
            >
                {(observerProps: { agentSpecifications: IAgentSpecification[]; errorMessage: string; isLoading: boolean }) => (
                    <AgentQueueSelector
                        agentSpecifications={[...observerProps.agentSpecifications]}
                        autoFocus={!this.props.isSaveEnabled}
                        isLoadingAgentSpecifications={observerProps.isLoading}
                        selectedAgentSpecification={this.state.selectedAgentSpecification}
                        onAgentSpecificationSelected={this._onSelectedAgentSpecificationChanged}
                        agentSpecificationErrorMessage={observerProps.errorMessage}
                        label={Resources.AgentPool}
                        agentQueues={this.state.queues}
                        selectedQueue={this._selectedAgentQueue}
                        onAgentChanged={this._onSelectedQueueChanged}
                    />
                )}
            </Observer>
        );
    }

    private _getBranchControl() {
        const buildRepository = this._buildDefinition ? this._buildDefinition.repository : undefined;
        const connectionId = buildRepository && buildRepository.properties ? buildRepository.properties["connectedServiceId"] || "" : "";
        const sourceSelectorLabel: string =
            this.props.buildRepository.type === RepositoryTypes.TfsVersionControl ? Resources.SourceVersionLabel : Resources.BranchLabel;
        const sourceVersion = this.state.sourceVersion;
        const isYamlBuildDefinition: boolean = this._buildDefinition ? this._buildDefinition.process.type === QueuePanel.YamlProcessType : false;

        const sourceSelectorDescription = this._isTfsGit ? Resources.TfsGitSourceSelectorDescription : Resources.GitSourceSelectorDescription;

        return (
            <BranchDropdown
                className="flex-column label margin-bottom-16"
                ariaLabel={sourceSelectorLabel}
                label={sourceSelectorLabel}
                autoFocus={isYamlBuildDefinition && !this.props.isSaveEnabled}
                repositoryType={this.props.buildRepository.type}
                repositoryId={this.props.buildRepository.id}
                defaultBranch={this._normalizeBranchName(this.props.sourceBranch)}
                description={sourceSelectorDescription}
                connectionId={connectionId}
                sourceVersion={sourceVersion}
                shelvesetName={this.state.shelvesetName}
                currentCommit={this.state.currentCommit}
                onBranchSelected={this._branchSelected}
                onBranchSelectedForCommit={this._branchSelectedForCommit}
                onError={this._onFetchRepoError}
                onVersionSelected={this._versionSelected}
                onTagSelected={this._tagSelected}
                onShelvesetSelected={this._shelvesetSelected}
                viewCommits={true}
                viewTags={true}
                pageContext={this.props.pageContext}
                actionHub={this._shelvesetPickerActionHub}
                isDrodownFullWidth={true}
                showBranchPickerOnCommitSelected={true}
                // Fix for BUG 1880996 - we need minHeight for correct display of empty dropdown in Firefox
                dropdownMinHeight={29}
            />
        );
    }

    private _onFetchRepoError = (errorMessage: string) => {
        this._showError = true;
        this._errorMessage = errorMessage;
        this._messageSeverity = MessageCardSeverity.Error;
        this.setState(this._getState());
    };

    private _getRunButtonText(): string {
        if (this.props.isSaveEnabled) {
            return this.state.messageSeverity !== MessageCardSeverity.Warning
                ? Resources.SaveAndQueueButtonText
                : Resources.SaveAndRunWithWarningsButtonText;
        } else {
            return this.state.messageSeverity !== MessageCardSeverity.Warning ? Resources.QueueButtonText : Resources.RunWithWarningsButtonText;
        }
    }

    private _onSaveCommentChange = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this._saveCommentText.value = newValue;
    };

    private _branchSelected = (branchString: string) => {
        this._sourceVersionActionCreator.updateBranch(branchString);
    };

    private _branchSelectedForCommit = (branchString: string) => {
        this._queueBuildStore.updateBranchForCommit(branchString);
    };

    private _versionSelected = (versionString: string) => {
        this._sourceVersionActionCreator.updateVersion(versionString);
    };

    private _tagSelected = (tagString: string) => {
        this._sourceVersionActionCreator.updateTag(tagString);
    };

    private _shelvesetSelected = (shelveset: string) => {
        this._sourceVersionActionCreator.updateShelveset(shelveset);
    };

    private _dismissPanel = () => {
        this._shortcutService.setShortcutsEnabled(true);
        this._showPanel = false;
        this.setState(this._getState());
        this.props.onDismiss();
    };

    private _storeChanged = () => {
        this._selectedAgentQueue = this._queueBuildStore.getSelectedQueue();
        this._sourceVersion = this._queueBuildStore.getSourceVersion();
        this._shelvesetName = this._queueBuildStore.getShelvesetName();
        this._currentCommit = this._queueBuildStore.getSourceVersion();
        this._enableDiagnostic = this._queueBuildStore.getEnableDiagnosticState();
        this.setState(this._getState());
    };

    private _queueStoreChanged = () => {
        this._queues = this._queueStore.getQueues() || [];
        this.setState(this._getState());
    };

    private _queueBuild = () => {
        this._shortcutService.setShortcutsEnabled(true);
        this._pendingOverlayTimeoutId = this._timerManagement.setTimeout(this._showOverlay, 250);

        this._runButtonDisabled.value = true;

        let promise = Promise.resolve(this._buildDefinition);
        let saving = false;

        if (this.props.isSaveEnabled && this.props.onSave) {
            promise = this.props.onSave(this._saveCommentText.value);
            saving = true;
        }

        const shelvesetName = this._queueBuildStore.getShelvesetName();

        const modifiedRepositoryResources = this._queueBuildStore.getModifiedRepositoryResources();
        let repositoryResources: { [key: string]: RepositoryResourceParameters } | undefined = undefined;
        if (modifiedRepositoryResources.length > 0) {
            repositoryResources = {};
            modifiedRepositoryResources.forEach(repository => {
                repositoryResources![repository.alias] = {
                    refName: repository.refName,
                    version: repository.version
                } as RepositoryResourceParameters;
            });
        }

        const modifiedPipelineResources = this._queueBuildStore.getModifiedPipelineResources();
        let pipelineResources: { [key: string]: PipelineResourceParameters } | undefined = undefined;
        if (modifiedPipelineResources.length > 0) {
            pipelineResources = {};
            modifiedPipelineResources.forEach(pipeline => {
                pipelineResources![pipeline.alias] = {
                    version: pipeline.version
                } as PipelineResourceParameters;
            });
        }

        const modifiedBuildResources = this._queueBuildStore.getModifiedBuildResources();
        let buildResources: { [key: string]: BuildResourceParameters } | undefined = undefined;
        if (modifiedBuildResources.length > 0) {
            buildResources = {};
            modifiedBuildResources.forEach(build => {
                buildResources![build.alias] = {
                    version: build.version
                } as BuildResourceParameters;
            });
        }

        const modifiedContainerResources = this._queueBuildStore.getModifiedContainerResources();
        let containerResources: { [key: string]: ContainerResourceParameters } | undefined = undefined;
        if (modifiedContainerResources.length > 0) {
            containerResources = {};
            modifiedContainerResources.forEach(container => {
                containerResources![container.alias] = {
                    version: container.version
                } as ContainerResourceParameters;
            });
        }

        const modifiedPackageResources = this._queueBuildStore.getModifiedPackageResources();
        let packageResources: { [key: string]: PackageResourceParameters } | undefined = undefined;
        if (modifiedPackageResources.length > 0) {
            packageResources = {};
            modifiedPackageResources.forEach(packageValue => {
                packageResources![packageValue.alias] = {
                    version: packageValue.version
                } as PackageResourceParameters;
            });
        }

        promise
            .then((updatedBuildDefinition: BuildDefinition) => {
                const selectedQueue = this._queueStore.getSelectedQueue();
                const selectedQueueId = selectedQueue ? selectedQueue.id : -1;
                const payload: IQueueBuildPayload = {
                    isYaml: this._isYamlBuildDefinition(),
                    definitionId: this.props.definitionId,
                    agentQueueId: selectedQueueId,
                    ignoreWarnings: this.state.messageSeverity === MessageCardSeverity.Warning,
                    projectId: this.props.projectId,
                    pageContext: this.props.pageContext,
                    variables: this._queueBuildStore.getRunTimeVariables(),
                    serializedVariables: this._queueBuildStore.getSerializedVariables(),
                    demands: this._queueBuildStore.getDemandsValues(),
                    sourceBranch: shelvesetName ? shelvesetName : this._queueBuildStore.getSourceBranch(),
                    sourceVersion: this._queueBuildStore.getSourceVersion() || undefined,
                    stagesToSkip: this._queueBuildStore.skippedStageNames,
                    repositoryResources: repositoryResources,
                    pipelineResources: pipelineResources,
                    buildResources: buildResources,
                    containerResources: containerResources,
                    packageResources: packageResources,
                    templateParameters: this._queueBuildStore.getTemplateParametersDictionary(),
                    agentSpecification: this._selectedAgentSpecification
                };

                if (
                    saving &&
                    !shelvesetName &&
                    updatedBuildDefinition &&
                    updatedBuildDefinition.repository &&
                    updatedBuildDefinition.repository.type.toLowerCase() === RepositoryTypes.TfsVersionControl.toLowerCase()
                ) {
                    // If saving a TFVC definition, then we need to get the saved definition's new defaultBranch for queuing since it is
                    // derived from the updated source mappings and if we send the old defaultBranch it can be incorrect
                    payload.sourceBranch = updatedBuildDefinition.repository.defaultBranch;
                }

                this._actionCreator.queueBuild(payload);
            })
            .catch(reason => {
                this._timerManagement.clearTimeout(this._pendingOverlayTimeoutId);
                let message = "";
                if (reason) {
                    // reason can be error with a string message or just a string
                    message = reason.message || reason;
                }
                this._showError = true;
                this._errorMessage = message;
                this._messageSeverity = MessageCardSeverity.Error;
                this._queueActionPending.value = false;
                this.setState(this._getState());
            });
    };

    private _onSelectedQueueChanged = (selectedQueue: TaskAgentQueue) => {
        if (selectedQueue) {
            this._selectedAgentSpecification = undefined;
            this._queueActionCreator.selectQueue(selectedQueue);
            this._updateAgentSpecifications(selectedQueue);
        }
    };

    private _onToggleEnableDiagnostic = (event: React.MouseEvent<HTMLElement>, checked: boolean) => {
        this._enableDiagnosticActionCreator.toggleEnableDiagnostic(checked);
    };

    private _onSelectedAgentSpecificationChanged = (agentSpecification: IAgentSpecification) => {
        this._selectedAgentSpecification = agentSpecification;
        this._agentSpecificationServiceErrorMessage.value = "";
        this._runButtonDisabled.value = false;
        this.setState(this._getState());
    };

    private _showOverlay = () => {
        if (this._showPanel) {
            this._queueActionPending.value = true;
        }
    };

    private _handleBuildQueued = (payload: IBuildQueuedPayload) => {
        this._publishQueueBuildTelemetry();
        this._timerManagement.clearTimeout(this._pendingOverlayTimeoutId);

        if (payload.buildId) {
            this._onSuccess(payload.buildId);
        } else {
            this._queueActionPending.value = false;
            this._showError = true;
            this._errorMessage = payload.message || "";
            this._messageSeverity = payload.messageSeverity || MessageCardSeverity.Info;
            this._runButtonDisabled.value = false;
            this.setState(this._getState());
        }
    };

    private _onSuccess = (queuedBuildId: number): void => {
        // Navigate to the run page
        const linkingService = this.props.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");
        const url = linkingService.getBuildUrl(queuedBuildId);
        onClickFPS(this.props.pageContext, url, true);
    };

    private _publishQueueBuildTelemetry() {
        const demands = this._queueBuildStore.getDemandsValues();
        const variables = this._queueBuildStore.getVariables();
        const eventProperties: IDictionaryStringTo<any> = {};
        eventProperties["variablesCount"] = variables ? variables.length : 0;
        eventProperties["customDemandsCount"] = demands ? demands.length : 0;
        eventProperties["buildDefinitionId"] = this.props.definitionId;

        const telemetryService = this.props.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("Build", "queueBuild", eventProperties);
    }

    private _observeElement = (element: HTMLDivElement | null) => {
        if (element && this._intersectionContext && !this._observingOverflow) {
            this._intersectionContext.observe(element);
            this._observedElement = element;
            this._observingOverflow = true;
        }
    };

    private _onIntersect = (entries: IntersectionObserverEntry[]) => {
        const entry = entries.find(e => e.target === this._observedElement);
        if (entry) {
            const overflowing = Math.round(entry.rootBounds!.bottom) < Math.round(entry.boundingClientRect.bottom);

            // only update state if overflow state has changed
            if (overflowing !== this._overflowing) {
                this._overflowing = overflowing;
                this.setState(this._getState());
            }
        }
    };

    private _registerAdvancedOptions = () => {
        const isYaml = this._isYamlBuildDefinition();
        const optionsToRender: IAdvancedOptionListItem[] = [];

        // variables list
        optionsToRender.push({
            component: VariablesControllerView,
            primaryText: Resources.VariablesTitle,
            props: {
                pageContext: this.props.pageContext,
                pipelineName: this._buildDefinition.name,
                store: this._queueBuildStore,
                enforceSettableVar: this._enforceSettableVar,
                allowEditing: this._enableEditPipelineQueueConfigurationPermission ? this._security.canEditPipelineQueueConfiguration(this._buildDefinition): true,
            } as IVariablesControllerViewProps,
            requiresFullYamlParse: false,
            secondaryText: (
                <Observer variables={this._queueBuildStore.runtimeVariables}>
                    {(props: { variables: IVariableData[] }) => (
                        <span>
                            {props.variables.length === 0
                                ? Resources.VariablesEmptySecondaryText
                                : props.variables.length === 1
                                ? Resources.VariablesOneSecondaryText
                                : format(Resources.VariablesManySecondaryText, props.variables.length)}
                        </span>
                    )}
                </Observer>
            )
        });
        
        // demands list
        if (!isYaml) {
            optionsToRender.push({
                component: DemandsControllerView,
                primaryText: Resources.DemandsTitle,
                props: {
                    pageContext: this.props.pageContext,
                    pipelineName: this._buildDefinition.name,
                    store: this._queueBuildStore
                } as IDemandsControllerViewProps,
                requiresFullYamlParse: false,
                secondaryText: (
                    <Observer demands={this._queueBuildStore.demands}>
                        {(props: { demands: IDemandData[] }) => (
                            <span>
                                {props.demands.length === 0
                                    ? Resources.DemandsEmptySecondaryText
                                    : props.demands.length === 1
                                    ? Resources.DemandsOneSecondaryText
                                    : format(Resources.DemandsManySecondaryText, props.demands.length)}
                            </span>
                        )}
                    </Observer>
                )
            });
        }

        // stage selection list
        if (isYaml && !this._stageChooserDisabled && this._wasDefinitionPreviouslySaved) {
            optionsToRender.push({
                component: StageOptionsControllerView,
                primaryText: Resources.StagesToRun,
                props: {
                    pageContext: this.props.pageContext,
                    store: this._queueBuildStore,
                    pipelineId: this.props.definitionId
                } as IStageOptionsControllerViewProps,
                requiresFullYamlParse: true,
                secondaryText: (
                    <Observer stageOptions={this._queueBuildStore.stageOptions}>
                        {(props: { stageOptions: IPipelineStageOption[] }) => {
                            const skippedStageCount = props.stageOptions.filter(s => s.skip).length;
                            const includedStageNames = props.stageOptions.filter(s => !s.skip).map(s => s.name);
                            return <span>{skippedStageCount === 0 ? Resources.StageOptionsRunAsConfigured : includedStageNames.join(", ")}</span>;
                        }}
                    </Observer>
                )
            });
        }

        // resources list
        if (
            isYaml &&
            this._wasDefinitionPreviouslySaved &&
            (this._queueBuildStore.repositoryResources ||
                this._queueBuildStore.pipelineResources ||
                this._queueBuildStore.buildResources ||
                this._queueBuildStore.containerResources ||
                this._queueBuildStore.packageResources)
        ) {
            optionsToRender.push({
                component: ResourcesControllerView,
                primaryText: Resources.Resources,
                props: {
                    pageContext: this.props.pageContext,
                    store: this._queueBuildStore,
                    pipelineId: this.props.definitionId
                } as IResourcesControllerViewProps,
                requiresFullYamlParse: true,
                secondaryText: (
                    <Observer
                        repositoryResources={this._queueBuildStore.repositoryResources}
                        pipelineResources={this._queueBuildStore.pipelineResources}
                        buildResources={this._queueBuildStore.buildResources}
                        containerResources={this._queueBuildStore.containerResources}
                        packageResources={this._queueBuildStore.packageResources}
                    >
                        {(props: {
                            repositoryResources: RepositoryResourceData[];
                            pipelineResources: PipelineResourceData[];
                            buildResources: BuildResourceData[];
                            containerResources: ContainerResourceData[];
                            packageResources: PackageResourceData[];
                        }) => {
                            let secondaryText = "";
                            if (this._queueBuildStore.runParametersAreLoaded) {
                                const repositories =
                                    props.repositoryResources &&
                                    props.repositoryResources.filter(repository => !equals(repository.alias, "self", true));
                                const pipelines = props.pipelineResources;
                                const builds: BuildResourceData[] = props.buildResources;
                                const containers: ContainerResourceData[] = props.containerResources;
                                const packages: PackageResourceData[] = props.packageResources;

                                const repositoryMessage = repositories
                                    ? repositories.length === 1
                                        ? Resources.ResourceCountRepository
                                        : format(Resources.ResourceCountRepostiories, repositories.length)
                                    : "";
                                const pipelineMessage = pipelines
                                    ? pipelines.length === 1
                                        ? Resources.ResourceCountPipelineRun
                                        : format(Resources.ResourceCountPipelineRuns, pipelines.length)
                                    : "";
                                const buildMessage = builds
                                    ? builds.length === 1
                                        ? Resources.ResourceCountBuildRun
                                        : format(Resources.ResourceCountBuildRuns, builds.length)
                                    : "";
                                const containerMessage = containers
                                    ? containers.length === 1
                                        ? Resources.ResourceCountContainerRun
                                        : format(Resources.ResourceCountContainerRuns, containers.length)
                                    : "";
                                const packageMessage = packages
                                    ? packages.length === 1
                                        ? Resources.ResourceCountPackageRun
                                        : format(Resources.ResourceCountPackageRuns, packages.length)
                                    : "";

                                if (repositoryMessage && pipelineMessage && buildMessage && packageMessage) {
                                    secondaryText = format(
                                        "{0}, {1}, {2}, {3}, {4}",
                                        repositoryMessage,
                                        pipelineMessage,
                                        buildMessage,
                                        containerMessage,
                                        packageMessage
                                    );
                                } else {
                                    secondaryText =
                                        repositoryMessage || pipelineMessage || buildMessage || packageMessage || Resources.ResourcesUseLatestText;
                                }
                            } else {
                                secondaryText = Resources.ResourcesUseLatestText;
                            }
                            return (
                                <Tooltip overflowOnly={true}>
                                    <span className="text-ellipsis">{secondaryText}</span>
                                </Tooltip>
                            );
                        }}
                    </Observer>
                )
            });
        }

        this._advancedOptionsItemProvider.value = optionsToRender;

        // disable options that rely on full YAML parse when template parameters aren't valid
        if (isYaml && this._queueBuildStore.templateParameters) {
            this._onTemplateParametersValidityUpdate(this._queueBuildStore.templateParametersAreValid.value);
            this._queueBuildStore.templateParametersAreValid.subscribe(this._onTemplateParametersValidityUpdate);
        }
    };

    private _onTemplateParametersValidityUpdate = (valid: boolean): void => {
        if (valid) {
            this._advancedOptionsListSelection.clearUnselectable();
        } else {
            // disable advanced options that may require template parameters
            const advancedOptions = this._advancedOptionsItemProvider.value;
            for (let i = 0; i < advancedOptions.length; i++) {
                if (advancedOptions[i].requiresFullYamlParse) {
                    this._advancedOptionsListSelection.addUnselectable(i);
                }
            }
        }
    };

    private _onAdvancedOptionSelected = (event: React.SyntheticEvent<HTMLElement, Event>, listRow: IListRow<IAdvancedOptionListItem>) => {
        const layoutManager = this.props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss: any) => {
            return React.createElement(listRow.data.component, { ...listRow.data.props, onDismiss: onDismiss });
        });
    };

    private _getState = () => {
        return {
            showPanel: this._showPanel,
            showError: this._showError,
            errorMessage: this._errorMessage,
            messageSeverity: this._messageSeverity,
            isRootPath: false,
            queues: this._queues,
            selectedAgentQueue: this._selectedAgentQueue,
            sourceVersion: this._sourceVersion,
            shelvesetName: this._shelvesetName,
            currentCommit: this._currentCommit,
            agentSpecifications: this._agentSpecifications ? this._agentSpecifications.value : [],
            selectedAgentSpecification: this._selectedAgentSpecification,
            isLoadingAgentSpecifications: this._isLoadingAgentSpecifications ? this._isLoadingAgentSpecifications.value : false,
            enableDiagnostic: this._enableDiagnostic,
            overflowing: this._overflowing
        };
    };

    private _normalizeBranchName(branchName: string) {
        if (branchName && branchName.startsWith(branchRefPrefix)) {
            return branchName.substr(branchRefPrefix.length);
        } else if (branchName && branchName.startsWith(tagRefPrefix)) {
            return branchName.substr(tagRefPrefix.length);
        }

        return branchName || "";
    }
}
